// campaign-template-merge.ts — what a tier-2 Save writes over a LIVE
// campaign's template (#103).
//
// `campaign-edit-commit` patches `{ template }`: a WHOLE-FIELD replace. So the
// object this returns IS the campaign's template afterwards — anything missing
// from it is deleted from a campaign that is mid-send.
//
// The editor models NINE fields (`drip-message-editor.tsx`'s mirror effect
// rebuilds the working template as exactly `_id, message, isPoll, poll, asset,
// imageId, sendLinkPreview, linkPreview, name`). `Template` carries more —
// `buttons`, `templateType`, `snippetShortcut`, and every inherited
// `UserMessage` field — so sending the nine alone silently stripped the rest.
// No campaign in prod uses `buttons` today, which is the only reason this was
// not a data-loss incident; it would become one the day a new template field
// ships.
//
// MERGE, don't replace. Two properties this relies on, both load-bearing:
//   - an explicit `undefined` in the working copy still WINS over the stored
//     value (spread copies own keys, present-with-undefined included), so
//     clearing an attachment really clears it and JSON drops the key on the
//     wire — a "skip undefined" merge would resurrect a removed image;
//   - the working copy's `_id` is the editor's scratch BRIDGE id
//     (`nextBridgeId()`), never a document id, so it is stripped — which
//     leaves the STORED template's own `_id` in place instead of deleting it.

//
// THE "CLEARED ASSET" PROPERTY HAS AN OFF-SITE DEPENDENCY. It holds only
// because the editor's mirror effect (`drip-message-editor.tsx` ~:106) rebuilds
// the working template as a FULL nine-key object literal every time, so a
// cleared asset arrives here as a present key holding `undefined` — which is
// what overrides the stored value. A future "only emit the keys that changed"
// optimisation there would turn that into an ABSENT key, and this merge would
// silently resurrect the deleted asset on a live campaign. Change one, check
// the other.

import type { DripStepTemplate } from '@common-reducers/DripEditorReducer';
import type { Template } from '@common/models/template';

/** `existing` is `campaign.template` as loaded; `working` is
 *  `DripEditorReducer.steps[0].template`, the editor's working copy —
 *  typed as what it really is (`Partial<Template> & {_id}`) rather than
 *  `Record<string, unknown>`, so a typo'd field name is a type error here
 *  instead of a field silently written to a live campaign under the wrong name.
 *  The single `as Template` that remains only widens `Partial` to full: the
 *  merge is total when `existing` is present, and when it is absent the caller
 *  (`campaign-edit-commit`) is patching a campaign that had no template at all. */
export function mergeEditedTemplate(
    existing: Template | null | undefined,
    working: DripStepTemplate,
): Template {
    const { _id: _bridgeId, ...edited } = working;
    return { ...(existing ?? {}), ...edited } as Template;
}
